import {
  FlowHumanTaskBlockFieldType,
  type FlowHumanTaskField,
  FlowHumanTaskFieldOptionsVariant,
} from 'flow-schema';
import { get } from 'lodash';
import { type ComponentType } from 'react';
import * as uuid from 'uuid';

import { assertNever } from 'common';

import { HumanTaskDateOnlyValueBlockFormFields } from './DateOnlyValue';
import { HumanTaskEditableValueBlockFormFields } from './EditableValue';
import { HumanTaskFileReferenceDownloadBlockFormFields } from './FileReferenceDownload';
import { HumanTaskFileUploadBlockFormFields } from './FileUpload';
import { HumanTaskImageUploadBlockFormFields } from './ImageUpload';
import { HumanTaskMarkdownBlockFormFields } from './Markdown';
import { HumanTaskOptionValueFormFields } from './OptionFields/OptionValue';
import { HumanTaskReadOnlyValueBlockFormFields } from './ReadOnlyValue';
import { type HumanTaskBlockFormFieldsProps } from './types';

export const HumanTaskFieldType = FlowHumanTaskBlockFieldType;
export type HumanTaskFieldType = FlowHumanTaskBlockFieldType;
export type HumanTaskField = FlowHumanTaskField;

export const HumanTaskFieldTypeNames: Record<FlowHumanTaskBlockFieldType, string> = {
  [HumanTaskFieldType.ContentMarkdown]: 'Free-Form Text Content',
  [HumanTaskFieldType.ReadOnlyValue]: 'Non-Editable Text',
  [HumanTaskFieldType.EditableValue]: 'Editable Text',
  [HumanTaskFieldType.FileUpload]: 'File Upload',
  [HumanTaskFieldType.ImageUpload]: 'Image Upload',
  [HumanTaskFieldType.DateOnlyValue]: 'Date',
  [HumanTaskFieldType.SingleOptionValue]: 'Single Select',
  [HumanTaskFieldType.MultiOptionValue]: 'Multi Select',
  [HumanTaskFieldType.FileReferenceDownload]: 'File Reference Download',
};

export const HUMAN_TASK_FIELDS_BY_TYPE: Record<
  FlowHumanTaskBlockFieldType,
  ComponentType<HumanTaskBlockFormFieldsProps>
> = {
  [HumanTaskFieldType.ContentMarkdown]: HumanTaskMarkdownBlockFormFields,
  [HumanTaskFieldType.ReadOnlyValue]: HumanTaskReadOnlyValueBlockFormFields,
  [HumanTaskFieldType.EditableValue]: HumanTaskEditableValueBlockFormFields,
  [HumanTaskFieldType.FileUpload]: HumanTaskFileUploadBlockFormFields,
  [HumanTaskFieldType.ImageUpload]: HumanTaskImageUploadBlockFormFields,
  [HumanTaskFieldType.DateOnlyValue]: HumanTaskDateOnlyValueBlockFormFields,
  [HumanTaskFieldType.SingleOptionValue]: HumanTaskOptionValueFormFields,
  [HumanTaskFieldType.MultiOptionValue]: HumanTaskOptionValueFormFields,
  [HumanTaskFieldType.FileReferenceDownload]: HumanTaskFileReferenceDownloadBlockFormFields,
};

export function getDefaultHumanTaskFieldConfig(
  fieldType: HumanTaskFieldType,
  options?: {
    currentValue: HumanTaskField | undefined;
  }
): FlowHumanTaskField {
  const { currentValue } = options ?? {};
  const defaultLabel = get(currentValue, 'label') ?? '';
  const defaultOptions = get(currentValue, 'options') ?? [
    {
      id: uuid.v4(),
      label: '',
    },
  ];
  const defaultVariableName = get(currentValue, 'variableName') ?? '';
  const defaultInitialValue = get(currentValue, 'initialValue') ?? '';
  // Only multi-select carries the array form, and only once a template has been parsed; every
  // other field authors a plain string, so switching type away from multi-select starts empty.
  const defaultStringInitialValue = Array.isArray(defaultInitialValue) ? '' : defaultInitialValue;
  const id = uuid.v4();
  switch (fieldType) {
    case HumanTaskFieldType.ContentMarkdown:
      return {
        id,
        fieldType,
        content: '',
      };
    case HumanTaskFieldType.ReadOnlyValue:
      return {
        id,
        fieldType,
        label: defaultLabel,
        value: '',
      };
    case HumanTaskFieldType.EditableValue:
      return {
        id,
        fieldType,
        label: defaultLabel,
        variableName: defaultVariableName,
        initialValue: defaultStringInitialValue,
      };
    case HumanTaskFieldType.FileUpload:
      return {
        id,
        fieldType,
        label: defaultLabel,
        variableName: defaultVariableName,
      };
    case HumanTaskFieldType.ImageUpload:
      return {
        id,
        fieldType,
        label: defaultLabel,
        variableName: defaultVariableName,
      };
    case HumanTaskFieldType.SingleOptionValue:
      return {
        id,
        fieldType,
        label: defaultLabel,
        optionsVariant:
          get(currentValue, 'optionsVariant') ?? FlowHumanTaskFieldOptionsVariant.Static,
        options: defaultOptions,
        dynamicOptions: get(currentValue, 'dynamicOptions') ?? '',
        variableName: defaultVariableName,
        initialValue: defaultStringInitialValue,
      };
    case HumanTaskFieldType.MultiOptionValue:
      return {
        id,
        fieldType,
        label: defaultLabel,
        optionsVariant:
          get(currentValue, 'optionsVariant') ?? FlowHumanTaskFieldOptionsVariant.Static,
        options: defaultOptions,
        dynamicOptions: get(currentValue, 'dynamicOptions') ?? '',
        variableName: defaultVariableName,
        initialValue: defaultInitialValue,
      };
    case HumanTaskFieldType.DateOnlyValue:
      return {
        id,
        fieldType,
        label: defaultLabel,
        variableName: defaultVariableName,
        initialValue: defaultStringInitialValue,
      };
    case HumanTaskFieldType.FileReferenceDownload:
      return {
        id,
        fieldType,
        label: defaultLabel,
        value: '',
      };
    default:
      assertNever('Unknown field type', fieldType);
  }
}
